import { EventEmitter }                                from '@angular/core';
import { BusinessPremisesEntityTooltipIdentifierEnum } from '@app/modules/business-premises/components/tooltip/business-premises-entity-tooltip/business-premises-entity-tooltip.model';
import {
	BusinessPremises,
	BusinessPremisesPresetEnum,
	BusinessPremisesStatusEnum
}                                                      from '@app/modules/business-premises/models/business-premises.model';
import { ErsPresetEnum }                               from '@app/modules/ers/models/ers.model';
import { TssPresetEnum }                               from '@app/modules/tss/models/tss.model';
import { UsagePeriodTypeEnum }                         from '@app/modules/usage/models/common-usage.model';
import { ReportTypeEnum }                              from '@app/modules/usage/models/usage-report.model';
import { UsageReportConfig }                           from '@app/modules/usage/presets/usage-base-config.model';
import { APP_CONFIG }                                  from '@appConfig/_app-config_master';
import { DeviceTypeEnum }                              from '@appConfig/media-config';
import { ITabContent }                                 from '@core/interfaces/common.interfaces';
import {
	FormDataTreatmentEnum,
	IFormCreateData,
	IFormData,
	IFormUpdateData
}                                                      from '@core/interfaces/form.interface';
import {
	BusinessPremisesScopesEnum,
	FeatureScopesEnum,
	OrganizationScopesEnum,
	ScopePermissionCheckEnum
}                                                      from '@models/scope.enums';
import { ActionEnum }                                  from '@shared/components/action-item/action-item.enum';
import { Address }                                     from '@shared/components/address/models/address.model';
import { IButtonGroupConfig }                          from '@shared/components/button-group/button-group.model';
import { CountriesEnum }                               from '@shared/components/country/models/country-country.model';
import { DownloadFormatEnum }                          from '@shared/components/download-button/download-button.model';
import {
	IStatusFlow,
	OutOfFlowDisplayModeEnum
}                                                      from '@shared/components/status-flow/status-flow.model';
import { IAddressFormConfig }                          from '@shared/forms/address-form/address-form.model';
import { SortOrderEnum }                               from '@shared/list-operations/components/sort-filter/sort-filter.model';
import { LIST_OPERATIONS_METADATA }                    from '@shared/list-operations/config/list-operations.config';
import {
	FilterIdentifiersEnum,
	FilterTypes,
	FilterValueType,
	IFilter
}                                                      from '@shared/list-operations/model/filter.model';
import {
	IListOperationsConfig,
	ListOperationsIdentifierEnum
}                                                      from '@shared/list-operations/model/list-operations.model';
import { PagingModeEnum }                              from '@shared/list-operations/model/paging.model';
import { ManagedObjectTypeEnum }                       from '@shared/models/managed-object.model';
import { actionStatus }                                from '@shared/services/toaster.service';
import { ObjectUtils }                                 from '@utils/helpers';

export interface IBusinessPremisesMetadata {
	buttonGroupConfigs: {
		list: IButtonGroupConfig;
		tab: IButtonGroupConfig;
	};
	entityId: string;
	entitySelection: {
		isAllowedOnBulkTaxationReportingCreation: ( businessPremises: BusinessPremises ) => boolean
	},
	entityTooltip: {
		allowedBusinessPremisesIdentifiers: BusinessPremisesEntityTooltipIdentifierEnum[];
		dateTimeFormat: Intl.DateTimeFormatOptions;
	};
	reportConfigs: {
		dataIntegrity: UsageReportConfig;
	};
	details: {
		dateFormat: Intl.DateTimeFormatOptions;
		internalTabContent: ITabContent[];
	};
	form: {
		addressFormConfig: IAddressFormConfig;
		baseData: {
			[ key: string ]: IFormData
		};
		createData: IFormCreateData[];
		updateData: IFormUpdateData[];
		preserveData: IFormUpdateData[];
	};
	i18n: {
		businessPremisesBasepath: string;
		page: string;
		entity: string;
		errors: string;
		form: string;
		entityDetails: string;
	};
	listOperationsBusinessPremises: IListOperationsConfig;
	listOperationsBulkTaxationReporting: IListOperationsConfig;
	listOperationsBusinessPremisesByOrganization: IListOperationsConfig;
	listOperationsBulkTaxationReportingByOrganization: IListOperationsConfig;
	status: IStatusFlow<BusinessPremisesStatusEnum, BusinessPremisesScopesEnum>;
	storeId: string;
}

const dataIntegrityReportI18nBasePath = `${ APP_CONFIG.businesspremises.i18nPath }.entity.details.reports.dataIntegrity`;

const MASTER_LIST_OPERATIONS_CONFIG: IListOperationsConfig = {
	id: ListOperationsIdentifierEnum.BUSINESS_PREMISES_LIST_PAGE,
	pagingData: {
		mode: PagingModeEnum.Light,
		continuesPagesMode: false,
		defaultValues: {
			pageSize: LIST_OPERATIONS_METADATA.default.paging.pageSize ?? 20,
			pageNumber: LIST_OPERATIONS_METADATA.default.paging.pageNumber ?? 0
		}
	},
	searchData: {
		placeholderI18nPath: 'businessPremises.search.placeholder'
	},
	sortData: [
		{ name: 'businessPremisesIdentifier', i18nPath: 'businessPremises.form.businessPremisesIdentifier.label' },
		{ name: 'name', i18nPath: 'businessPremises.sort.name.label' },
		{ name: 'addressData.street', i18nPath: 'shared.forms.address.street.label' },
		{ name: 'addressData.postalCode', i18nPath: 'shared.forms.address.postalCode.label' },
		{ name: 'addressData.city', i18nPath: 'shared.forms.address.city.label' },
		{ name: 'createdDate', i18nPath: 'businessPremises.sort.createdDate.label' },
		{ name: 'lastModifiedDate', i18nPath: 'businessPremises.sort.lastModifiedDate.label', defaultActive: true, sortOrder: SortOrderEnum.DESC },
		{
			name: 'lastSuccessRegistrationProtocolPreviewDate',
			i18nPath: 'businessPremises.sort.lastSuccessRegistrationProtocolPreviewDate.label',
			defaultActive: true,
			sortOrder: SortOrderEnum.DESC
		},
	],
	filterData: [
		{
			order: 1,
			isRendered: true,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.FILTER,
			type: FilterTypes.FILTER_TEXT_TYPE,
			i18nPath: 'businessPremises.filter.queryFilter',
			items: [
				{ i18nPath: 'businessPremises.filter.queryFilter' }
			]
		},
		{
			order: 2,
			isRendered: true,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.STATUS,
			type: FilterTypes.FILTER_LIST_TYPE,
			i18nPath: 'businessPremises.filter.status',
			values: [ BusinessPremisesStatusEnum.ACTIVE ],
			items: [
				{ value: BusinessPremisesStatusEnum.ACTIVE, i18nPath: 'shared.status.active' },
				{ value: BusinessPremisesStatusEnum.DISABLED, i18nPath: 'shared.status.disabled' }
			]
		},
		{
			order: 3,
			isRendered: true,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.BUSINESS_PREMISES_IDS,
			type: FilterTypes.FILTER_SELECT_ERS_TYPE,
			selectFilter: {
				paramKey: 'businessPremisesId',
				presetId: ErsPresetEnum.ERS,
				searchRestoreValueKey: 'ersIdentifier'
			},
			i18nPath: 'businessPremises.filter.ers',
			service: 'ers',
			bindLabel: 'ersIdentifier',
			bindValue: 'ersIdentifier',
			resetEventEmitter: new EventEmitter<boolean>(),
			values: [],
			items: [],
			tooltip: {
				bindKey: 'ersIdentifier'
			}
		},
		{
			order: 4,
			isRendered: true,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.ORGANIZATION_IDS,
			type: FilterTypes.FILTER_SELECT_ORGANIZATION_TYPE,
			selectFilter: {
				paramKey: 'id',
				searchRestoreValueKey: 'name'
			},
			i18nPath: 'businessPremises.filter.organization',
			service: 'organization',
			bindLabel: 'name',
			bindValue: 'name',
			resetEventEmitter: new EventEmitter<boolean>(),
			values: [],
			items: [],
			tooltip: {
				bindKey: 'name'
			},
			permissionConfig: {
				requiredScope: OrganizationScopesEnum.List,
				revokeScopeOnManagedObjectTypes: [
					ManagedObjectTypeEnum.BUSINESS_PREMISES,
					ManagedObjectTypeEnum.ORGANIZATION
				]
			}
		},
		{
			order: 5,
			isRendered: true,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.BUSINESS_PREMISES_IDS,
			type: FilterTypes.FILTER_SELECT_TSS_TYPE,
			selectFilter: {
				paramKey: 'businessPremisesId',
				presetId: TssPresetEnum.TSS,
				searchRestoreValueKey: 'fccId'
			},
			i18nPath: 'businessPremises.filter.tss',
			service: 'tss',
			bindLabel: 'name',
			bindValue: 'name',
			resetEventEmitter: new EventEmitter<boolean>(),
			values: [],
			items: [],
			tooltip: {
				bindKey: 'name'
			}
		},
		{
			order: 6,
			isRendered: true,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.HAS_VALIDATION_RESULTS,
			type: FilterTypes.FILTER_SWITCH_TYPE,
			i18nPath: 'shared.filters.hasValidationResults'
		},
		{
			order: 7,
			isRendered: true,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.IS_NOT_YET_TAX_REPORTED,
			type: FilterTypes.FILTER_SWITCH_TYPE,
			i18nPath: 'businessPremises.filter.isNotYetTaxReported'
		},
		{
			order: 8,
			isRendered: false,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.HAS_ERRORS,
			type: FilterTypes.FILTER_SWITCH_TYPE,
			i18nPath: ''
		}
	]
};


const listOperationsConfigBusinessPremises = (): IListOperationsConfig => {
	return { ...MASTER_LIST_OPERATIONS_CONFIG };
};

const listOperationsConfigBulkTaxationReporting = ( isOrgDetailPageTab?: boolean ): IListOperationsConfig => {
	const filterWhiteList: FilterIdentifiersEnum[] = [
		FilterIdentifiersEnum.HAS_ERRORS,
		FilterIdentifiersEnum.IS_NOT_YET_TAX_REPORTED,
		FilterIdentifiersEnum.STATUS,
		...isOrgDetailPageTab ? [] : [ FilterIdentifiersEnum.ORGANIZATION_IDS ]
	];

	const filterData: IFilter[] = MASTER_LIST_OPERATIONS_CONFIG.filterData
	.filter( ( filter: IFilter ) => filterWhiteList.includes( filter.filterId ) )
	.map( ( filter: IFilter ) => {
		let values: FilterValueType[] = filter.values;
		switch ( filter.filterId ) {
			case FilterIdentifiersEnum.STATUS:
				values = [ ...filter.values, BusinessPremisesStatusEnum.DISABLED ];
				break;
			case FilterIdentifiersEnum.HAS_ERRORS:
				values = [ false ];
				break;
		}

		return { ...filter, values };
	} );

	return { ...MASTER_LIST_OPERATIONS_CONFIG,
		id: isOrgDetailPageTab
				? ListOperationsIdentifierEnum.ORGANIZATION_DETAIL_PAGE_BULK_TAXATION_REPORTING
				: ListOperationsIdentifierEnum.BUSINESS_PREMISES_LIST_PAGE_BULK_TAXATION_REPORTING,
		filterData,
		searchData: null
	};
};

const listOperationsConfigBusinessPremisesByOrganization = (): IListOperationsConfig => {
	const filterWhiteList: FilterIdentifiersEnum[] = [
		FilterIdentifiersEnum.IS_NOT_YET_TAX_REPORTED,
		FilterIdentifiersEnum.HAS_VALIDATION_RESULTS,
		FilterIdentifiersEnum.STATUS
	];

	const filterData: IFilter[] = MASTER_LIST_OPERATIONS_CONFIG.filterData
	.filter( ( filter: IFilter ) => filterWhiteList.includes( filter.filterId ) );

	return {
		...MASTER_LIST_OPERATIONS_CONFIG,
		id: ListOperationsIdentifierEnum.ORGANIZATION_DETAIL_PAGE_BUSINESS_PREMISES_TAB_LIST,
		filterData,
		searchData: null
	};
};

export const BUSINESS_PREMISES_METADATA: IBusinessPremisesMetadata = ObjectUtils.deepCopyByRecursion( {
	buttonGroupConfigs: {
		list: {
			items: [
				{ enforceLabelCustomTemplate: true, id: BusinessPremisesPresetEnum.BUSINESS_PREMISES, order: 1 },
				{ enforceLabelCustomTemplate: true, id: BusinessPremisesPresetEnum.BULK_TAXATION_REPORTING, order: 2 }
			]
		},
		tab: {
			items: [
				{ enforceLabelCustomTemplate: true, id: BusinessPremisesPresetEnum.ORGANIZATION_DETAIL_BUSINESS_PREMISES, order: 1 },
				{ enforceLabelCustomTemplate: true, id: BusinessPremisesPresetEnum.ORGANIZATION_DETAIL_BULK_TAXATION_REPORTING, order: 2 }
			]
		}
	},
	entityId: APP_CONFIG.businesspremises.entityId,
	entitySelection: {
		isAllowedOnBulkTaxationReportingCreation: ( businessPremises: BusinessPremises ) => {
			const countryWhiteList: CountriesEnum[] = [
				CountriesEnum.GERMANY
			];
			return countryWhiteList.includes( businessPremises.addressData.countryCode as CountriesEnum );
		},
		// isAllowedOnBulkTaxationReportingSubmission: ( businessPremises: BusinessPremises ) => {
		// 	return !businessPremises?.ui.hasSeveralTaxationReportingsWithStatusReady;
		// }
	},
	entityTooltip: {
		allowedBusinessPremisesIdentifiers: [
			BusinessPremisesEntityTooltipIdentifierEnum.NAME,
			BusinessPremisesEntityTooltipIdentifierEnum.IDENTIFIER,
			BusinessPremisesEntityTooltipIdentifierEnum.ADDRESS,
			BusinessPremisesEntityTooltipIdentifierEnum.STATUS,
			BusinessPremisesEntityTooltipIdentifierEnum.CREATED_DATE,
			BusinessPremisesEntityTooltipIdentifierEnum.LAST_MODIFIED_DATE,
			BusinessPremisesEntityTooltipIdentifierEnum.DESCRIPTION
		],
		dateTimeFormat: { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }
	},
	reportConfigs: {
		dataIntegrity: {
			...new UsageReportConfig(),

			// actions
			downloadFormats: [
				DownloadFormatEnum.CSV
			],
			download: {
				actionEventId: null,
				emitAction: false,
				isDisabled: false,
				isHidden: false,
				isRendered: true
			},
			request: {
				actionEventId: null,
				emitAction: false,
				isDisabled: false,
				isHidden: false,
				isRendered: true
			},
			retry: {
				actionEventId: null,
				emitAction: false,
				isDisabled: false,
				isHidden: false,
				isRendered: true
			},
			regenerate: {
				actionEventId: null,
				emitAction: false,
				isDisabled: false,
				isHidden: false,
				isRendered: true
			},

			// contents
			currentMonthReportIsSupported: true,
			description: {
				isDisabled: false,
				isHidden: false,
				isRendered: true
			},
			reportType: ReportTypeEnum.DATA_INTEGRITY,
			showPageLoader: false,
			status: {
				isDisabled: false,
				isHidden: false,
				isRendered: true
			},
			statusDescription: {
				isDisabled: false,
				isHidden: false,
				isRendered: true
			},
			usagePeriodType: UsagePeriodTypeEnum.YEAR_MONTH_DAY,

			// store
			keepCurrentPeriodCached: true,

			// i18n
			statusSuccessDescriptionPathPrefix: dataIntegrityReportI18nBasePath
		}
	},
	details: {
		dateFormat: { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false },
		internalTabContent: [
			{
				key: 'tenantId',
				order: 1,
				i18nPath: 'businessPremises.details.tenantId',
				addSpaceToPrevious: false,
				showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ],
				copyClipboard: true
			},
			{
				key: 'id',
				order: 2,
				i18nPath: 'businessPremises.details.id',
				addSpaceToPrevious: false,
				showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ],
				copyClipboard: true
			},
			{
				key: 'parentOrgId',
				order: 3,
				i18nPath: 'businessPremises.details.parentOrgId',
				addSpaceToPrevious: false,
				showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ],
				copyClipboard: true
			}
		]
	},
	form: {
		addressFormConfig: {
			country: {
				allowedCountries: [
					CountriesEnum.AUSTRIA,
					CountriesEnum.GERMANY,
					CountriesEnum.SWITZERLAND
				],
				showOnly: 'currentAndAllowed'
			}
		},
		baseData: {
			additionalData: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'shared.forms.address.additionalData',
				order: 7,
				specialTreatments: null
			},
			businessPremisesIdentifier: {
				addSpaceToPrevious: false,
				hide: false,
				i18nPath: APP_CONFIG.businesspremises.i18nPath + '.form.businessPremisesIdentifier',
				order: 3,
				showInfo: true,
				specialTreatments: null
			},
			city: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterRequiredField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'shared.forms.address.city',
				order: 8,
				required: true,
				specialTreatments: null
			},
			countryCode: {
				addSpaceToPrevious: false,
				hide: false,
				i18nPath: 'businessPremises.form.address.countryCode',
				order: 11,
				specialTreatments: [ FormDataTreatmentEnum.COUNTRY_CODE_TO_I18N ]
			},
			detailedDescription: {
				addSpaceToPrevious: false,
				hide: false,
				i18nPath: 'shared.forms.detailedDescription',
				order: 4,
				specialTreatments: null
			},
			houseNumber: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'shared.forms.address.houseNumber',
				order: 6,
				permissionData: {
					scopes: [ FeatureScopesEnum.ERIC ],
					permissionCheck: ScopePermissionCheckEnum.AND
				},
				required: true,
				specialTreatments: [ FormDataTreatmentEnum.HAS_PERMISSION ]
			},
			id: {
				hide: true,
				i18nPath: '',
				order: null,
				specialTreatments: null
			},
			name: {
				addSpaceToPrevious: true,
				elsterField: true,
				elsterRequiredField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: APP_CONFIG.businesspremises.i18nPath + '.form.name',
				order: 2,
				showInfo: true,
				specialTreatments: null
			},
			parentOrgId: {
				addSpaceToPrevious: false,
				hide: false,
				i18nPath: 'shared.forms.parentOrgId',
				order: 1,
				specialTreatments: [ FormDataTreatmentEnum.ORG_ID_TO_NAME ]
			},
			postalCode: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterRequiredField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'businessPremises.form.address.postalCode',
				order: 9,
				required: true,
				specialTreatments: null
			},
			state: {
				addSpaceToPrevious: false,
				hide: false,
				i18nPath: 'shared.forms.address.state',
				order: 10,
				specialTreatments: null
			},
			status: {
				hide: false,
				i18nPath: 'shared.forms.status',
				order: 18,
				specialTreatments: [ FormDataTreatmentEnum.STATUS_TO_I18N ]
			},
			street: {
				addSpaceToPrevious: true,
				elsterField: true,
				elsterRequiredField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'shared.forms.address.street',
				order: 5,
				required: true,
				specialTreatments: null
			},
			taxOfficeRemark: {
				addSpaceToPrevious: true,
				elsterField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'businessPremises.form.taxOfficeRemark',
				maxLength: 1000,
				order: 12,
				permissionData: {
					scopes: [ FeatureScopesEnum.ERIC ],
					permissionCheck: ScopePermissionCheckEnum.AND
				},
				specialTreatments: [ FormDataTreatmentEnum.HAS_PERMISSION ]
			}
		},
		createData: [
			{ key: 'addressData', val: new Address() }
		],
		updateData: [],
		preserveData: [
			{ key: 'createdDate', fallbackValue: null },
			{ key: 'etag', fallbackValue: null },
			{ key: 'id', fallbackValue: null },
			{ key: 'lastModifiedDate', fallbackValue: null },
			{ key: 'parentOrgId', fallbackValue: null },
			{ key: 'status', fallbackValue: null }
		]
	},
	i18n: {
		businessPremisesBasepath: APP_CONFIG.businesspremises.i18nPath,
		page: 'page',
		entity: 'entity',
		errors: 'errors',
		form: 'form',
		entityDetails: 'entity.details'
	},
	listOperationsBusinessPremises: listOperationsConfigBusinessPremises(),
	listOperationsBulkTaxationReporting: listOperationsConfigBulkTaxationReporting(),
	listOperationsBusinessPremisesByOrganization: listOperationsConfigBusinessPremisesByOrganization(),
	listOperationsBulkTaxationReportingByOrganization: listOperationsConfigBulkTaxationReporting( true ),
	status: {
		actions: [
			{
				actionItem: {
					action: ActionEnum.ACTIVATE,
					labelI18n: 'shared.buttons.activate',
					tooltipI18n: 'shared.buttons.activate',
					scopes: BusinessPremisesScopesEnum.STATUS_ENABLE
				},
				dialog: {
					i18nPathButtonConfirm: 'activate',
					i18nPathDescription: 'businessPremises.entity.dialogForActive.description'
				},
				enforceDisableOnStatuses: [],
				enforceHideOnStatuses: [],
				order: 1,
				outOfFlowDisplayMode: OutOfFlowDisplayModeEnum.DISABLED,
				requestActionStatus: actionStatus.ACTIVATE,
				targetStatus: BusinessPremisesStatusEnum.ACTIVE
			},
			{
				actionItem: {
					action: ActionEnum.DISABLE,
					labelI18n: 'shared.buttons.disable',
					tooltipI18n: 'shared.buttons.disable',
					scopes: BusinessPremisesScopesEnum.STATUS_DISABLE
				},
				dialog: {
					i18nPathButtonConfirm: 'disable',
					i18nPathDescription: 'businessPremises.entity.dialogForDisabled.description'
				},
				enforceDisableOnStatuses: [],
				enforceHideOnStatuses: [],
				order: 3,
				outOfFlowDisplayMode: OutOfFlowDisplayModeEnum.DISABLED,
				requestActionStatus: actionStatus.DISABLE,
				targetStatus: BusinessPremisesStatusEnum.DISABLED
			}
		],
		flow: {
			[ BusinessPremisesStatusEnum.ACTIVE ]: [ BusinessPremisesStatusEnum.DISABLED, BusinessPremisesStatusEnum.DELETED ],
			[ BusinessPremisesStatusEnum.DELETED ]: [],
			[ BusinessPremisesStatusEnum.DISABLED ]: [ BusinessPremisesStatusEnum.ACTIVE, BusinessPremisesStatusEnum.DELETED ]
		}
	},
	storeId: APP_CONFIG.businesspremises.store.id
} );
